<template>
  <div class="mt-5 custom-scroll">
    <div class="d-flex align-items-center flex-wrap gap-3">
      <div class="flex-grow-1">
        <h2 class="dash-title">Dashboard Restaurante</h2>
        <p class="dash-subtitle">Análisis y KPIs en tiempo real</p>
      </div>
      <div class="filters-group">
        <select v-model="groupBy" @change="fetchDashboard" id="group-select">
          <option v-for="opt in groupByOptions" :key="opt.value" :value="opt.value">
            {{ opt.label }}
          </option>
        </select>
        <input type="date" v-model="startDate" @change="fetchDashboard" class="date-input"/>
        <span class="mx-1">–</span>
        <input type="date" v-model="endDate" @change="fetchDashboard" class="date-input"/>
      </div>
    </div>
    <div class="kpis-grid">
      <KpiCard label="Ingresos Hoy" :value="kpis.revenueToday + ' €'">
        <template #icon><DollarSign class="w-8 h-8 text-orange" /></template>
      </KpiCard>

      <KpiCard label="Tickets Totales" :value="kpis.ticketsTotal">
        <template #icon><ShoppingCart class="w-8 h-8 text-blue" /></template>
      </KpiCard>

      <KpiCard label="Ticket Medio" :value="kpis.ticketAvg + ' €'">
        <template #icon><BarChart3 class="w-8 h-8 text-green" /></template>
      </KpiCard>

      <KpiCard label="Sesiones QR" :value="kpis.qrSessions">
        <template #icon><Activity class="w-8 h-8 text-purple" /></template>
      </KpiCard>

      <KpiCard label="Ocupación" :value="kpis.occupancy + '%'">
        <template #icon><MapPin class="w-8 h-8 text-cyan" /></template>
      </KpiCard>

      <KpiCard label="Rotación" :value="kpis.turnover">
        <template #icon><RotateCcw class="w-8 h-8 text-indigo" /></template>
      </KpiCard>
    </div>
    <div v-if="hasData">
      <div class="row mt-3">
        <div class="col-md-6 mb-2">
          <AreaCard :options="charts.incomeArea" :payments="paymentSummary" />
        </div>
        <div class="col-md-6 mb-2">
          <SessionsPanel :sessions="sessions" />
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-md-6 mb-2">
          <DonutCard :options="charts.salesDonut" :legend="legends.sales" />
        </div>
        <div class="col-md-6 mb-2">
          <ServiceTimesCard :metrics="serviceTimes" metaText="10 min" />
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-md-6 mb-2">
          <TopDishesTable v-model:mode="dishesMode" :rows="dishesMode === 'units' ? topDishesUnits : topDishesIncome" />
        </div>
        <div class="col-md-6 mb-2">
          <BarCard :options="charts.ticketsByChannel" :legend="legends.tickets" />
        </div>
      </div>
      <div class="row mt-3" v-if="charts.ordersArea && charts.ordersArea.series && charts.ordersArea.series.length">
        <OrdersPeaksCard
          :highlights="highlights"
          :chartData="charts.ordersArea"
          :scope="groupBy"
          :startDate="startDate"
          :endDate="endDate"
        />
      </div>
    </div>
    <div v-else class="no-chart-global">
      <img :src="no_charts" alt="Sin datos" />
      <p class="no-chart-text">No hay datos en el rango especificado</p>
    </div>
  </div>
</template>

<script>
  import '@/styles/dashboard.css';
  import axios from 'axios';
  import { mapState } from 'vuex';
  import { makeAreaTimeseries, makeOrdersTimeseries, makeDonut, makeBars } from '@/charts/configFactory.js';
  import { DollarSign, ShoppingCart, BarChart3, Activity, MapPin, RotateCcw } from "lucide-vue-next";
  import no_charts from '@/assets/no_charts.svg';

  import KpiCard from '@/components/dashboard/KpiCard.vue';
  import AreaCard from '@/components/dashboard/AreaCard.vue';
  import SessionsPanel from '@/components/dashboard/SessionsPanel.vue';
  import DonutCard from '@/components/dashboard/DonutCard.vue';
  import ServiceTimesCard from '@/components/dashboard/ServiceTimesCard.vue';
  import OrdersPeaksCard from '@/components/dashboard/OrdersPeaksCard.vue';
  import TopDishesTable from '@/components/dashboard/TopDishesTable.vue';
  import BarCard from '@/components/dashboard/BarCard.vue';

  export default {
    name: 'DashboardView',
    components: {
      KpiCard, AreaCard, SessionsPanel, DonutCard, ServiceTimesCard,
      OrdersPeaksCard, TopDishesTable, BarCard,
      DollarSign, ShoppingCart, BarChart3, Activity, MapPin, RotateCcw
    },
    data() {
      return {
        no_charts,
        groupBy: 'hour',
        startDate: new Date().toISOString().slice(0, 10),
        endDate: new Date().toISOString().slice(0, 10),
        groupByOptions: [
          { value: "quarter", label: "Cuartohorario" },
          { value: "hour", label: "Horario" },
          { value: "day", label: "Diario" },
          { value: "month", label: "Mensual" },
          { value: "year", label: "Anual" },
        ],
        kpis: {
          revenueToday: '', ticketsTotal: '', ticketAvg: '',
          qrSessions: '', occupancy: '', tablesUsed: '',
          tablesTotal: '', turnover: ''
        },
        sessions: [],
        legends: { sales: [], tickets: [] },
        serviceTimes: [],
        highlights: {
          highPeak: { label: "", delta: "", detail: "" },
          lowValley: { label: "", delta: "", detail: "" },
          avg: { label: "", delta: "", detail: "" },
        },
        charts: {
          incomeArea: { series: [] },
          salesDonut: { series: [] },
          ticketsByChannel: { series: [] },
          ordersArea: { series: [] },
        },
        paymentSummary: { cash: 0, card: 0, bizum: 0 },
        topDishesUnits: [],
        topDishesIncome: [],
        dishesMode: 'units',
        hasData: false
      };
    },
    computed: { ...mapState(['partner_token']) },
    methods: {
      formatDateLabel(dateStr) {
        const d = new Date(dateStr);
        if (this.groupBy === "hour") return d.toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit" });
        if (this.groupBy === "day") return d.toLocaleString("es-ES", { day: "2-digit", month: "short" });
        if (this.groupBy === "month") return d.toLocaleString("es-ES", { month: "short", year: "numeric" });
        if (this.groupBy === "year") return d.getFullYear().toString();
        return dateStr;
      },

      async fetchDashboard() {
        const url = process.env.VUE_APP_API_URL + "/v1/api/app/getDashboard";
        const headers = this.partner_token ? { Authorization: `Bearer ${this.partner_token}` } : {};

        const { data } = await axios.get(url, {
          headers,
          params: { start_date: this.startDate, end_date: this.endDate, group_by: this.groupBy }
        });

        // KPIs
        this.kpis.revenueToday = (data?.total_income_today || 0).toFixed(2);
        this.kpis.ticketsTotal = String(data?.total_tickets_today || 0);
        this.kpis.ticketAvg = (data?.mean_ticket_today || 0).toFixed(2);
        this.kpis.occupancy = String(data?.occupation_percent || 0);
        this.kpis.qrSessions = data?.qr_sessions || 0;
        this.kpis.turnover = data?.avg_sessions_per_table || 0;

        if (data.income_by_period && data.income_by_period.length > 0) {
          this.hasData = true;

          // Ingresos por periodo
          this.charts.incomeArea = makeAreaTimeseries({
            data: data.income_by_period,
            startDate: this.startDate,
            endDate: this.endDate,
            scope: this.groupBy,
            color: "#ff7a59",
            yTitle: "Ingresos (€)"
          });

          // Métodos de pago
          this.paymentSummary = {
            cash: data?.total_cash_period || 0,
            card: data?.total_card_period || 0,
            bizum: data?.total_bizum_period || 0,
          };

          // Pedidos por periodo
          this.charts.ordersArea = makeOrdersTimeseries({
            data: data.orders_by_period,
            startDate: this.startDate,
            endDate: this.endDate,
            scope: this.groupBy,
            color: "#b9a9ff",
            yTitle: "Pedidos"
          });

          // Highlights
          this.highlights = data?.highlights || this.highlights;

          // Ventas por canal
          const salesByChannel = data?.sales_by_channel || {};
          const salesSeries = [{
            name: "Ventas",
            data: [
              { name: "Sala", y: salesByChannel.local || 0 },
              { name: "Delivery", y: salesByChannel.delivery || 0 },
              { name: "TakeAway", y: salesByChannel.takeaway || 0 },
            ]
          }];

          this.charts.salesDonut = makeDonut({ series: salesSeries });
          this.legends.sales = [
            { label: "Sala", value: (salesByChannel.local || 0).toFixed(2), color: "#ff7a59" },
            { label: "Delivery", value: (salesByChannel.delivery || 0).toFixed(2), color: "#3b82f6" },
            { label: "TakeAway", value: (salesByChannel.takeaway || 0).toFixed(2), color: "#22c55e" },
          ];

          // Tickets por canal
          const ticketChannels = data?.tickets_by_channel || {};
          this.charts.ticketsByChannel = makeBars({
            categories: Object.keys(ticketChannels),
            series: [{ name: "Tickets", data: Object.values(ticketChannels) }],
            color: "#6d6af7",
          });
          this.legends.tickets = Object.keys(ticketChannels).map(c => ({
            label: c,
            value: ticketChannels[c] || 0,
            color: "#6d6af7"
          }));

          // Tiempos de servicio
          this.serviceTimes = [
            { label: "Primer Pedido", value: data.avg_first_order_minutes.toFixed(1), caption: "Desde inicio de QR", progress: Math.min((data.avg_first_order_minutes / 10) * 100, 100) },
            { label: "Aceptación", value: data.avg_accept_minutes.toFixed(1), caption: "Pendiente → En curso", progress: Math.min((data.avg_accept_minutes / 5) * 100, 100) },
            { label: "Servir 1º Plato", value: data.avg_first_serve_minutes.toFixed(1), caption: "En curso → Entregado", progress: Math.min((data.avg_first_serve_minutes / 20) * 100, 100) },
            { label: "Tiempo Total", value: data.avg_total_session_minutes.toFixed(1), caption: "Inicio → Liberación", progress: Math.min((data.avg_total_session_minutes / 60) * 100, 100) },
          ];

          //Top platos
          this.topDishesUnits = data?.top_dishes_units || [];
          this.topDishesIncome = data?.top_dishes_income || [];

          // Sesiones
          this.sessions = data?.sessions || [];

        } else {
          this.hasData = false;
        }
      },
    },

    async mounted() {
      await this.fetchDashboard();
    }
  };
</script>
<style scoped>
  #group-select {
    padding: 6px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: white;
    cursor: pointer;
    font-size: 14px;
  }

  .kpis-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 16px;
    margin-top: 20px;
  }

  .no-chart-global {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: calc(100vh - 140px);
    text-align: center;
    gap: 12px;
  }

  .no-chart-global img {
    max-width: 80%;
    max-height: 60vh;
    object-fit: contain;
    opacity: 0.8;
  }

  .no-chart-text {
    font-size: 16px;
    color: #6b7280;
    margin-top: 10px;
  }

  .filters-group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .date-input {
    padding: 6px 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: white;
    font-size: 14px;
    cursor: pointer;
  }

  .custom-scroll {
    overflow-x: hidden;
    width: 100%;
  }

  .custom-scroll .row {
    margin-right: 0 !important;
    margin-left: 0 !important;
  }

  .custom-scroll [class*="col-"] {
    padding-left: 8px;
    padding-right: 8px;
    box-sizing: border-box;
  }

  @media (max-width: 1024px) {
    .kpis-grid { 
      grid-template-columns: repeat(3, 1fr); 
    }
  }

  @media (max-width: 768px) {
    .kpis-grid { 
      grid-template-columns: repeat(2, 1fr); 
    }

    .filters-group { 
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: flex-start;
      gap: 6px;
    }

    .date-input { 
      width: 30%;
    }

    #group-select {
      width: 25%;
    }
  }
</style>
